import { CLIENT_COLORS, TASK_STATUS_COLORS, PRI_DOT } from "../constants";
import { isoToday, curMonth, fmtDate } from "../utils";
import { Dot, Tag, Card, Empty, ProgressBar, Stage, KpiStrip } from "../components/primitives";

// Score: inclui bônus de plano mensal; exclui AC/AT das penalidades
function calcScore(client, tasks, historico, anotacoes, planos, today, month) {
  let score = 100;

  // Apenas tarefas pontuais (não AC/AT) para penalidades de prazo
  const cTasks = tasks.filter(
    (t) => t.client_id === client.id && t.status !== "Concluído" && !t.archived
      && t.type !== "AC" && t.type !== "AT"
  );
  const late = cTasks.filter((t) => t.due_date && t.due_date < today).length;
  score -= Math.min(late * 12, 30);

  // Plano mensal: penaliza ausência, bônus proporcional ao progresso
  const planoMes = planos.find((p) => p.client_id === client.id && p.month === month);
  if (!planoMes) {
    score -= 15;
  } else {
    const planoTotal = planoMes.items.length;
    const planoDone  = planoMes.items.filter((i) => i.done).length;
    const planoPct   = planoTotal ? Math.round((planoDone / planoTotal) * 100) : 0;
    if      (planoPct === 100) score += 20;
    else if (planoPct >= 80)   score += 10;
    else if (planoPct >= 50)   score += 5;
  }

  // Histórico mensal
  const hasHistorico = historico.some((h) => h.client_id === client.id && h.mes === month);
  if (!hasHistorico) score -= 10;

  // Anotações recentes
  const clientAno = anotacoes
    .filter((a) => a.client_id === client.id)
    .sort((a, b) => b.data.localeCompare(a.data));
  if (clientAno.length === 0) {
    score -= 15;
  } else {
    const days = Math.floor((new Date() - new Date(clientAno[0].data + "T12:00:00")) / 86400000);
    if      (days > 30) score -= 15;
    else if (days > 15) score -= 7;
  }

  // Tarefas pontuais urgentes atrasadas (apenas non-AC/AT)
  const highPriLate = cTasks.filter(
    (t) => t.priority === "Alta" && t.due_date && t.due_date < today
  ).length;
  score -= Math.min(highPriLate * 8, 16);

  return Math.max(0, Math.min(100, score));
}

// Threshold: saudável ≥ 70 (era 80)
function ScoreBadge({ score }) {
  const [bg, color, label] =
    score >= 70 ? ["var(--success-soft)", "var(--success-ink)", "Saudável"]
    : score >= 50 ? ["var(--warn-soft)",    "var(--warn-ink)",    "Atenção"]
    : ["var(--danger-soft)",  "var(--danger-ink)",  "Risco"];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 5,
      background: bg, borderRadius: "var(--r-pill)",
      padding: "2px 8px", flexShrink: 0,
    }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      <span style={{ fontSize: 10, fontWeight: 700, color }}>{score}</span>
      <span style={{ fontSize: 10, color, opacity: .8 }}>{label}</span>
    </div>
  );
}

function TaskItem({ task, clients, today, onGoClient }) {
  const client = clients.find((c) => c.id === task.client_id);
  const sc     = TASK_STATUS_COLORS[task.status] || TASK_STATUS_COLORS["A fazer"];
  const done   = (task.checklist || []).filter((x) => x.done).length;
  const total  = (task.checklist || []).length;
  const late   = task.due_date && task.due_date < today;

  return (
    <div
      className="card card-hover"
      onClick={() => client && onGoClient(client.id)}
      style={{ padding: "12px 16px", marginBottom: 8, cursor: client ? "pointer" : "default" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Dot color={PRI_DOT[task.priority] || "var(--faint)"} size={7} style={{ marginTop: 5, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{task.name}</span>
            <Tag label={task.type} bg={sc.bg} color={sc.tx} xs />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {client && (
              <span style={{ fontSize: 11, color: "var(--primary)", fontWeight: 500 }}>{client.name}</span>
            )}
            {task.due_date && (
              <span style={{ fontSize: 11, color: late ? "var(--danger)" : "var(--faint)" }}>
                · {fmtDate(task.due_date)}{late ? " ⚠" : ""}
              </span>
            )}
            {total > 0 && (
              <span style={{ fontSize: 11, color: done === total ? "var(--success)" : "var(--faint)" }}>
                · {done}/{total} itens
              </span>
            )}
          </div>
          {total > 0 && <div style={{ marginTop: 6 }}><ProgressBar done={done} total={total} /></div>}
        </div>
      </div>
    </div>
  );
}

export function RotinaDoDiaView({ clients, tasks, events, planos = [], historico = [], anotacoes = [], onGoClient }) {
  const today    = isoToday();
  const month    = curMonth();
  const todayDay = new Date().getDate();

  const nextWeekDate = new Date();
  nextWeekDate.setDate(nextWeekDate.getDate() + 7);
  const nextWeekStr = nextWeekDate.toISOString().split("T")[0];

  const nowStr = new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  // ── Tarefas pontuais (não-AC/AT): únicas com due_date real ─────────────────
  const PRI_ORDER = { Alta: 0, Média: 1, Baixa: 2 };
  const inactiveClientIds = new Set(
    clients.filter((c) => c.status === "Pausado" || c.status === "Encerrado").map((c) => c.id)
  );
  const active      = tasks.filter((t) => t.status !== "Concluído" && !t.archived && !inactiveClientIds.has(t.client_id));
  const pontual     = active.filter((t) => t.type !== "AC" && t.type !== "AT");
  const todayTasks  = pontual.filter((t) => t.due_date === today);
  const overdue     = pontual
    .filter((t) => t.due_date && t.due_date < today)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  const weekTasks   = pontual
    .filter((t) => t.due_date && t.due_date > today && t.due_date <= nextWeekStr)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  const noDueDateTasks = pontual
    .filter((t) => !t.due_date)
    .sort((a, b) => (PRI_ORDER[a.priority] ?? 3) - (PRI_ORDER[b.priority] ?? 3));

  // ── Eventos de hoje ─────────────────────────────────────────────────────────
  const todayEvents = events.filter((e) => e.event_date === today);

  // ── Clientes ativos (sem Internos) ──────────────────────────────────────────
  const activeClients = clients.filter((c) => c.status === "Ativo" && c.type !== "Interno");

  // ── Operação Mensal ─────────────────────────────────────────────────────────
  const operacaoClientes = activeClients.map((c) => {
    const planoMes   = planos.find((p) => p.client_id === c.id && p.month === month);
    const planoTotal = planoMes ? planoMes.items.length : 0;
    const planoDone  = planoMes ? planoMes.items.filter((i) => i.done).length : 0;
    const planoPct   = planoTotal ? Math.round((planoDone / planoTotal) * 100) : null;

    const acTask  = tasks.find((t) => t.client_id === c.id && t.type === "AC" && t.month === month && !t.archived);
    const acTotal = acTask ? (acTask.checklist || []).length : 0;
    const acDone  = acTask ? (acTask.checklist || []).filter((x) => x.done).length : 0;

    const score = calcScore(c, tasks, historico, anotacoes, planos, today, month);
    return { client: c, planoPct, planoDone, planoTotal, acDone, acTotal, score };
  });

  // ── Métricas para KPI cards ─────────────────────────────────────────────────
  const semAcEstaSemana = operacaoClientes.filter((x) => x.acTotal > 0 && x.acDone === 0);
  const clientesEmRisco = operacaoClientes.filter((x) => x.score < 50);
  const planoAbaixo50   = operacaoClientes.filter((x) => x.planoTotal > 0 && x.planoPct < 50);
  const planoCompleto   = operacaoClientes.filter((x) => x.planoPct === 100);

  // ── Clientes — atenção ──────────────────────────────────────────────────────
  const clientAttention = activeClients
    .map((c) => {
      const cPontual    = pontual.filter((t) => t.client_id === c.id);
      const urgentCount = cPontual.filter((t) => t.priority === "Alta" && t.due_date && t.due_date < today).length;
      const lateCount   = cPontual.filter((t) => t.due_date && t.due_date < today && t.priority !== "Alta").length;
      const noPlano     = !planos.some((p) => p.client_id === c.id && p.month === month);
      const diff        = c.dia_relatorio ? c.dia_relatorio - todayDay : null;
      const relatorio   = diff !== null && diff >= 0 && diff <= 2;
      const score       = calcScore(c, tasks, historico, anotacoes, planos, today, month);

      const acTask  = tasks.find((t) => t.client_id === c.id && t.type === "AC" && t.month === month && !t.archived);
      const acTotal = acTask ? (acTask.checklist || []).length : 0;
      const acDone  = acTask ? (acTask.checklist || []).filter((x) => x.done).length : 0;

      const reasons = [];
      if (urgentCount > 0)          reasons.push({ icon: "🔴", text: `${urgentCount} tarefa${urgentCount > 1 ? "s" : ""} urgente${urgentCount > 1 ? "s" : ""}`, level: "danger" });
      if (lateCount  > 0)           reasons.push({ icon: "⚠️",  text: `${lateCount} tarefa${lateCount > 1 ? "s" : ""} em atraso`, level: "warn" });
      if (noPlano)                  reasons.push({ icon: "📋",  text: "Sem plano este mês", level: "warn" });
      if (acTotal > 0 && acDone === 0) reasons.push({ icon: "📅", text: "Semana sem progresso na AC", level: "warn" });
      if (relatorio)                reasons.push({ icon: "📊",  text: diff === 0 ? "Relatório hoje" : `Relatório em ${diff}d`, level: "info" });

      return { client: c, score, reasons };
    })
    .filter((x) => x.reasons.length > 0 || x.score < 70)
    .sort((a, b) => a.score - b.score);

  const levelColor = { danger: "var(--danger-ink)", warn: "var(--warn-ink)", info: "var(--primary)" };

  return (
    <div className="view">

      <Stage
        path="rotina-do-dia"
        title="Rotina do"
        hl="dia"
        lead={nowStr.charAt(0).toUpperCase() + nowStr.slice(1)}
      />

      {/* ── KPIs em faixa ──────────────────────────────────────────────────── */}
      <KpiStrip style={{ marginBottom: 28 }} items={[
        { label: "Sem AC esta semana", value: semAcEstaSemana.length, sub: "clientes sem progresso", tone: semAcEstaSemana.length ? "warn" : "ok", color: semAcEstaSemana.length ? "var(--warn-ink)" : "var(--faint)" },
        { label: "Em risco", value: clientesEmRisco.length, sub: "score abaixo de 50", tone: clientesEmRisco.length ? "bad" : "ok", color: clientesEmRisco.length ? "var(--danger-ink)" : "var(--faint)" },
        { label: "Plano abaixo 50%", value: planoAbaixo50.length, sub: "abaixo do esperado", tone: planoAbaixo50.length ? "warn" : "ok", color: planoAbaixo50.length ? "var(--warn-ink)" : "var(--faint)" },
        { label: "Plano 100%", value: planoCompleto.length, sub: "concluídos este mês", tone: "ok", color: planoCompleto.length ? "var(--success-ink)" : "var(--faint)" },
      ]} />

      {/* ── Conteúdo principal ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* Coluna esquerda */}
        <div style={{ flex: 2, minWidth: 300 }}>

          {/* Sem progresso esta semana */}
          {semAcEstaSemana.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--warn-ink)" }}>Sem progresso esta semana</span>
                <span className="badge badge-warn">{semAcEstaSemana.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {semAcEstaSemana.map(({ client, acDone, acTotal, planoPct, score }) => {
                  const col = CLIENT_COLORS[client.type] || CLIENT_COLORS["Interno"];
                  return (
                    <div
                      key={client.id}
                      className="card card-hover"
                      onClick={() => onGoClient(client.id)}
                      style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <Dot color={col.dot} size={7} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {client.name}
                      </span>
                      {acTotal > 0 && (
                        <span style={{ fontSize: 11, color: "var(--warn-ink)", fontWeight: 600 }}>AC: {acDone}/{acTotal}</span>
                      )}
                      {planoPct !== null && (
                        <span style={{ fontSize: 11, color: "var(--muted)" }}>Plano: {planoPct}%</span>
                      )}
                      <ScoreBadge score={score} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tarefas pontuais com due_date */}
          {todayTasks.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Hoje</span>
                <span className="badge badge-primary">{todayTasks.length}</span>
              </div>
              {todayTasks.map((t) => (
                <TaskItem key={t.id} task={t} clients={clients} today={today} onGoClient={onGoClient} />
              ))}
            </div>
          )}

          {overdue.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--danger)" }}>Atrasadas</span>
                <span className="badge badge-danger">{overdue.length}</span>
              </div>
              {overdue.slice(0, 10).map((t) => (
                <TaskItem key={t.id} task={t} clients={clients} today={today} onGoClient={onGoClient} />
              ))}
              {overdue.length > 10 && (
                <div style={{ fontSize: 12, color: "var(--faint)", padding: "8px 0" }}>
                  + {overdue.length - 10} tarefas adicionais em atraso
                </div>
              )}
            </div>
          )}

          {weekTasks.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Esta semana</span>
                <span className="badge badge-neutral">{weekTasks.length}</span>
              </div>
              {weekTasks.slice(0, 12).map((t) => (
                <TaskItem key={t.id} task={t} clients={clients} today={today} onGoClient={onGoClient} />
              ))}
            </div>
          )}

          {noDueDateTasks.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Em aberto</span>
                <span className="badge badge-neutral">{noDueDateTasks.length}</span>
                <span style={{ fontSize: 11, color: "var(--faint)" }}>sem prazo definido</span>
              </div>
              {noDueDateTasks.map((t) => (
                <TaskItem key={t.id} task={t} clients={clients} today={today} onGoClient={onGoClient} />
              ))}
            </div>
          )}

          {/* ── Operação Mensal ──────────────────────────────────────────────── */}
          {operacaoClientes.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Operação Mensal</span>
                <span className="badge badge-neutral">{operacaoClientes.length} clientes</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {operacaoClientes.map(({ client, planoPct, planoDone, planoTotal, acDone, acTotal, score }) => {
                  const col      = CLIENT_COLORS[client.type] || CLIENT_COLORS["Interno"];
                  const pct      = planoPct ?? 0;
                  const barColor = pct === 100 ? "var(--success)" : pct >= 60 ? "var(--primary)" : pct >= 30 ? "var(--warn)" : "var(--danger)";
                  const acColor  = acDone === acTotal && acTotal > 0 ? "var(--success-ink)" : acDone === 0 && acTotal > 0 ? "var(--warn-ink)" : "var(--ink)";
                  return (
                    <div
                      key={client.id}
                      className="card card-hover"
                      onClick={() => onGoClient(client.id)}
                      style={{ padding: "14px 18px", cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: planoPct !== null ? 10 : 0 }}>
                        <Dot color={col.dot} size={7} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {client.name}
                        </span>
                        {acTotal > 0 && (
                          <span style={{ fontSize: 11, color: "var(--muted)", flexShrink: 0 }}>
                            Semana: <strong style={{ color: acColor }}>{acDone}/{acTotal}</strong>
                          </span>
                        )}
                        <ScoreBadge score={score} />
                      </div>
                      {planoPct !== null ? (
                        <div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>
                              Plano mensal — {planoDone}/{planoTotal} itens
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{pct}%</span>
                          </div>
                          <div style={{ height: 5, background: "var(--line-soft)", borderRadius: 99, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 99, transition: "width .3s" }} />
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: "var(--warn-ink)", marginTop: 4 }}>
                          📋 Sem plano para este mês
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* Coluna direita — eventos + clientes */}
        <div style={{ flex: 1, minWidth: 260 }}>

          {/* Eventos de hoje */}
          {todayEvents.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>Eventos de hoje</div>
              <div className="klist">
                {todayEvents.map((e) => (
                  <div key={e.id} className="klist-row">
                    <span className="txt" style={{ fontWeight: 600 }}>{e.title}</span>
                    {e.event_time && <span className="aside">{e.event_time.slice(0, 5)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clientes — atenção */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span className="eyebrow">Clientes em atenção</span>
              {clientAttention.length > 0 && <span className="pill warn">{clientAttention.length}</span>}
            </div>

            {clientAttention.length === 0 ? (
              <div className="alert-line ok">Todos os clientes saudáveis ✓</div>
            ) : (
              <div className="klist">
                {clientAttention.map(({ client, score, reasons }) => {
                  const col = CLIENT_COLORS[client.type] || CLIENT_COLORS["Interno"];
                  const r0 = reasons[0];
                  return (
                    <div
                      key={client.id}
                      className="klist-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => onGoClient(client.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onGoClient(client.id); } }}
                      style={{ cursor: "pointer" }}
                      title={reasons.map((r) => r.text).join(" · ")}
                    >
                      <Dot color={col.dot} size={7} />
                      <span className="txt" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ fontWeight: 600 }}>{client.name}</span>
                        {r0 && <span style={{ color: levelColor[r0.level], fontSize: 12.5 }}> · {r0.text}{reasons.length > 1 ? ` (+${reasons.length - 1})` : ""}</span>}
                      </span>
                      <ScoreBadge score={score} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
